import { LockClosedIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode } from "react";
import Cta from "ui/organisms/Navbar/Cta";
import useAuth from "util/useAuth/useAuth";
import Divider from "../Divider";
import LockedOverlay from "../LockedOverlay";

type Themes = "default" | "yellow";

interface Props {
  title?: string | JSX.Element;
  subtitle?: string | JSX.Element;
  children: ReactNode;
  containerPadding?: string;
  mobileHeadingPadding?: string;
  mobileDivider?: boolean;
  containerClassName?: string;
  mobile?: boolean;
  locked?: boolean;
  theme?: Themes;
}

const themes: {
  [key in Themes]: {
    title: string;
    subtitle: string;
    shadow: string;
    panel: string;
  };
} = {
  default: {
    title: "bg-slate-300 text-black",
    subtitle: "text-white",
    shadow: "bg-slate-500",
    panel: "border-slate-300 bg-black",
  },
  yellow: {
    title: "bg-yellow-500 text-white",
    subtitle: "text-yellow-500",
    shadow: "bg-yellow-700",
    panel: "border-yellow-500 bg-black",
  },
};

export default function TabContainer({
  title,
  subtitle,
  children,
  containerPadding = "p-2",
  mobileHeadingPadding = "pt-3 pb-3 px-3",
  containerClassName,
  mobileDivider,
  mobile,
  locked,
  theme = "default",
}: Props) {
  const auth = useAuth();
  const hasTitleTabs = title && !mobile;

  return (
    <article className="z-0 mx-1 mt-1">
      {hasTitleTabs && (
        <div className="hidden lg:inline-flex">
          <div className="relative z-0 inline-flex">
            <h3
              className={classNames(
                "inline-block rounded-t px-3 pb-2 pt-1 font-brik text-xl",
                themes[theme].title
              )}
            >
              {title}
            </h3>

            <div
              className={classNames(
                "absolute inset-0 -z-10 -translate-x-1 translate-y-1 rounded-tl ",
                themes[theme].shadow
              )}
            />
          </div>

          {subtitle && (
            <h4
              className={classNames(
                "relative mt-1 inline-block self-end rounded-tr border-2 px-2 pb-1 align-bottom ",
                themes[theme].panel,
                themes[theme].subtitle
              )}
            >
              {subtitle}
            </h4>
          )}
        </div>
      )}

      <div className="relative -top-1 z-0">
        <div
          className={classNames(
            containerClassName,
            "relative z-10 rounded border-2",
            themes[theme].panel,
            hasTitleTabs && "lg:rounded-tl-none",

            locked ? "pointer-events-none" : ""
          )}
        >
          {title && (
            <div
              className={classNames(
                "text-center text-white",
                !mobile && "lg:hidden",
                mobileHeadingPadding
              )}
            >
              <h3 className="font-brik text-3xl leading-none">{title}</h3>

              {subtitle && <h4 className="text-md leading-none">{subtitle}</h4>}
            </div>
          )}

          {mobileDivider && <Divider className={"my-3 md:hidden"} />}

          <div className={containerPadding}>{children}</div>
        </div>

        <div
          className={classNames(
            "absolute inset-0 -z-10 -translate-x-1 translate-y-1 rounded ",
            hasTitleTabs && "lg:rounded-t-none",
            themes[theme].shadow
          )}
        />

        <LockedOverlay locked={!!locked} auth={auth} />
      </div>
    </article>
  );
}
